import { useCallback, useMemo, useState } from 'react';
import {
  acwrOutOfOptimalRange,
  classifyAcwr,
  classifyDeltaPct,
  classifyMonotony,
  SRPE_BINS,
} from '@leopart/calc';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { formatFullName } from '../../../utils/functions';
import { roleLabel } from '../../utils/roleLabel';
import { AthleteListRow } from '../AthleteListModal/AthleteListModal.types';
import { IEffortDay, IEffortRow, IEffortTeamKpi, IEffortTeamResponse } from './effort.types';

// ─────────────────────────────────────────────────────────────────────────────
// Soglie e helper puri (spec 2026-07-26-tab-sforzo-design.md)
// ─────────────────────────────────────────────────────────────────────────────

export type EffortViewMode = 'srpe' | 'rpe';
export type EffortBin = 0 | 1 | 2 | 3 | 4;

/** Bin colore AU/giorno: ≤250, 251–450, 451–650, 651–850, >850 (fonte: @leopart/calc) */
export const SRPE_BIN_UPPER_BOUNDS = SRPE_BINS;
/** Bin per modalità RPE medio (1–10): ≤2, ≤4, ≤6, ≤8, >8 */
export const RPE_BIN_UPPER_BOUNDS = [2, 4, 6, 8] as const;

export function getSrpeBin(value: number): EffortBin {
  const idx = SRPE_BIN_UPPER_BOUNDS.findIndex((max) => value <= max);
  return (idx === -1 ? 4 : idx) as EffortBin;
}

export function getRpeBin(value: number): EffortBin {
  const idx = RPE_BIN_UPPER_BOUNDS.findIndex((max) => value <= max);
  return (idx === -1 ? 4 : idx) as EffortBin;
}

export function getEffortBin(mode: EffortViewMode, value: number): EffortBin {
  return mode === 'srpe' ? getSrpeBin(value) : getRpeBin(value);
}

export type AcwrStatus = 'ok' | 'warn' | 'alert';

/** Verde 0.80–1.20, ambra 1.20–1.30, rosso >1.30 o <0.80 ("non allenante") */
export function getAcwrStatus(acwr: number): AcwrStatus {
  // Soglie da @leopart/calc (fonte unica, stesse zone in tutta l'app)
  return classifyAcwr(acwr);
}

export type DeltaStatus = 'neutral' | 'warn' | 'alert';

/** Nero |Δ|≤15, ambra 15<|Δ|≤50, rosso >50 */
export function getDeltaStatus(deltaPct: number): DeltaStatus {
  return classifyDeltaPct(deltaPct);
}

export type MonotonyStatus = 'neutral' | 'warn';

/** Nero ≤2.0, ambra >2.0 */
export function getMonotonyStatus(monotony: number): MonotonyStatus {
  return classifyMonotony(monotony) === 'warn' ? 'warn' : 'neutral';
}

/** Lunedì (YYYY-MM-DD) della settimana contenente `date` — settimana lun–dom */
export function getMondayOf(date: Dayjs): string {
  return date.subtract((date.day() + 6) % 7, 'day').format('YYYY-MM-DD');
}

/**
 * Righe per la modale "Atleti fuori range" (click sul KPI `outOfRangeCount`):
 * stesso predicato del conteggio KPI lato backend (effort.service.ts —
 * `acwrValues.filter(acwrOutOfRange)`) — `acwrOutOfOptimalRange` da
 * @leopart/calc è la fonte unica delle soglie ACWR, mai ridichiarate in locale.
 */
export function getOutOfRangeAthleteRows(rows: IEffortRow[]): AthleteListRow[] {
  return rows
    .filter(
      (r): r is IEffortRow & { acwr: number } =>
        r.acwr != null && acwrOutOfOptimalRange(r.acwr),
    )
    .map((r) => ({
      athleteId: r.athlete._id,
      name: formatFullName(r.athlete.firstName, r.athlete.lastName),
      role: roleLabel(r.athlete.role),
      metricValue: `ACWR ${r.acwr.toFixed(2)}`,
      tone: classifyAcwr(r.acwr),
    }));
}

// ─────────────────────────────────────────────────────────────────────────────
// Normalizzazione difensiva della risposta BE
// ─────────────────────────────────────────────────────────────────────────────

function numOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function normalizeDay(raw: any): IEffortDay {
  const srpe = numOrNull(raw?.srpe);
  const rpeAvg = numOrNull(raw?.rpeAvg);
  return {
    date: String(raw?.date ?? ''),
    srpe,
    rpeAvg,
    missing: raw?.missing === true || (srpe == null && rpeAvg == null),
    eventLabel: raw?.eventLabel ?? null,
  };
}

function normalizeRow(raw: any): IEffortRow {
  const athlete = raw?.athlete ?? {};
  return {
    athlete: {
      _id: String(athlete?._id ?? athlete?.athleteId ?? raw?.athleteId ?? ''),
      firstName: athlete?.firstName ?? '',
      lastName: athlete?.lastName ?? '',
      // Il BE (effort.service.ts toEffortAthlete) espone la posizione atleta
      // sotto la chiave "role" del contratto — già sourced da user.position.
      // Nessun fallback su un ipotetico athlete.position: il wire non lo manda.
      role: athlete?.role ?? undefined,
    },
    days: Array.isArray(raw?.days) ? raw.days.map(normalizeDay) : [],
    weekTotal: numOrNull(raw?.weekTotal) ?? 0,
    deltaPct: numOrNull(raw?.deltaPct),
    acwr: numOrNull(raw?.acwr),
    monotony: numOrNull(raw?.monotony),
  };
}

/**
 * Normalizza la risposta grezza di `GET /effort/team/:teamId?week=` al contratto
 * `IEffortTeamResponse` (alias KPI difensivi, righe/giorni tipizzati).
 *
 * Esportata perché `useTeamDailyLoadRecovery` (vista Settimana Allenamenti)
 * condivide la STESSA chiave di cache TanStack Query (`['team-effort', teamId,
 * week]`): entrambi i queryFn devono scrivere in cache la stessa forma
 * normalizzata, altrimenti chi popola per primo la cache decide cosa legge
 * l'altro — se la Settimana scrivesse il raw, la tab Sforzo leggerebbe
 * `kpi.avgLoad` a `undefined` (il BE manda `avgWeeklyLoad`).
 */
export function normalizeTeamEffort(raw: any): IEffortTeamResponse {
  const k = raw?.kpi ?? {};
  const kpi: IEffortTeamKpi = {
    avgLoad: numOrNull(k.avgLoad ?? k.teamAvgLoad ?? k.avgWeeklyLoad),
    avgAcwr: numOrNull(k.avgAcwr ?? k.acwrAvg),
    outOfRangeCount: numOrNull(k.outOfRangeCount ?? k.outOfRange ?? k.athletesOutOfRange) ?? 0,
    avgMonotony: numOrNull(k.avgMonotony ?? k.monotonyAvg),
  };
  return {
    kpi,
    rows: Array.isArray(raw?.rows) ? raw.rows.map(normalizeRow) : [],
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook — tutta la business logic della vista team
// ─────────────────────────────────────────────────────────────────────────────

export function useTeamEffort(teamId: string) {
  const endpoints = useEndpoints();

  // Lunedì della settimana visualizzata — default settimana corrente (lun–dom)
  const [week, setWeek] = useState<string>(() => getMondayOf(dayjs()));
  const [mode, setMode] = useState<EffortViewMode>('srpe');

  const query = useQuery<IEffortTeamResponse>({
    queryKey: ['team-effort', teamId, week],
    queryFn: async () => {
      const { data } = await endpoints.getTeamEffort(teamId, { week });
      return normalizeTeamEffort(data);
    },
    staleTime: 1000 * 60 * 5,
  });

  const currentMonday = getMondayOf(dayjs());
  const isCurrentWeek = week === currentMonday;

  const goPrevWeek = useCallback(() => {
    setWeek((w) => dayjs(w).subtract(7, 'day').format('YYYY-MM-DD'));
  }, []);

  const goNextWeek = useCallback(() => {
    setWeek((w) => {
      const next = dayjs(w).add(7, 'day').format('YYYY-MM-DD');
      // Non oltre la settimana corrente
      return next > getMondayOf(dayjs()) ? w : next;
    });
  }, []);

  const goCurrentWeek = useCallback(() => {
    setWeek(getMondayOf(dayjs()));
  }, []);

  // 7 giorni (lun–dom) della settimana visualizzata
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => dayjs(week).add(i, 'day')),
    [week],
  );

  const weekLabel = useMemo(
    () => `${weekDays[0].format('D MMM')} – ${weekDays[6].format('D MMM YYYY')}`,
    [weekDays],
  );

  const rows = useMemo(() => query.data?.rows ?? [], [query.data]);
  const kpi = query.data?.kpi ?? null;

  // Etichetta seduta per colonna giorno: prima etichetta non vuota tra le righe
  const dayEventLabels = useMemo(() => {
    return weekDays.map((d) => {
      const iso = d.format('YYYY-MM-DD');
      for (const row of rows) {
        const day = row.days.find((x) => x.date === iso);
        if (day?.eventLabel) return day.eventLabel;
      }
      return null;
    });
  }, [weekDays, rows]);

  // Max settimanale squadra → barra proporzionale colonna Sett.
  const maxWeekTotal = useMemo(
    () => rows.reduce((max, r) => Math.max(max, r.weekTotal ?? 0), 0),
    [rows],
  );

  return {
    // stato
    week,
    weekDays,
    weekLabel,
    isCurrentWeek,
    mode,
    setMode,
    // navigazione
    goPrevWeek,
    goNextWeek,
    goCurrentWeek,
    // dati
    kpi,
    rows,
    dayEventLabels,
    maxWeekTotal,
    // query state
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
